<template>
  <div class="repair-return-visit-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('repairReturnVisit.listTitle')" :subtitle="$t('repairReturnVisit.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.repairId" clearable class="w-full"
            :placeholder="$t('repairReturnVisit.repairIdPlaceholder')" @keyup.enter.native="queryRepairPool" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.repairType" clearable class="w-full"
            :placeholder="$t('repairReturnVisit.repairTypePlaceholder')">
            <el-option v-for="item in repairSettings" :key="item.repairType" :label="item.repairTypeName"
              :value="item.repairType" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.repairName" clearable class="w-full"
            :placeholder="$t('repairReturnVisit.repairNamePlaceholder')" @keyup.enter.native="queryRepairPool" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.tel" clearable class="w-full"
            :placeholder="$t('repairReturnVisit.telPlaceholder')" @keyup.enter.native="queryRepairPool" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.state" clearable class="w-full"
            :placeholder="$t('repairReturnVisit.statePlaceholder')">
            <el-option v-for="item in states" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="queryRepairPool">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetRepairPool">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="listRepairPools">
        <template #left>
          <span class="ds-panel__title">{{ $t('repairReturnVisit.listTitle') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="repairPools" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('repairReturnVisit.page.empty')" @retry="listRepairPools">
        <el-table-column prop="repairId" :label="$t('repairReturnVisit.repairId')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.repairId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="repairObjName" :label="$t('repairReturnVisit.location')" align="center" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.repairObjName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="repairTypeName" :label="$t('repairReturnVisit.repairType')" align="center" width="130">
          <template slot-scope="scope">
            {{ scope.row.repairTypeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="repairName" :label="$t('repairReturnVisit.repairName')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.repairName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="tel" :label="$t('repairReturnVisit.contact')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.tel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="appointmentTime" :label="$t('repairReturnVisit.appointmentTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.appointmentTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="180">
          <template slot-scope="scope">
            <el-button v-if="!scope.row.visitId" size="mini" type="primary" @click="openEditOwnerRepair(scope.row)">
              {{ $t('repairReturnVisit.visit') }}
            </el-button>
            <el-button size="mini" @click="openRepairDetail(scope.row)">
              {{ $t('repairReturnVisit.detail') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <visit-owner-repair ref="visitModal" @success="listRepairPools" />
  </div>
</template>

<script>
import { getRepairReturnVisitList, listRepairSettings } from '@/api/work/repairReturnVisitApi'
import VisitOwnerRepair from '@/components/work/visitOwnerRepair'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'RepairReturnVisitList',
  components: {
    VisitOwnerRepair,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        repairId: '',
        repairType: '',
        repairName: '',
        tel: '',
        state: 'waiting'
      },
      repairPools: [],
      repairSettings: [],
      states: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  async created() {
    await this.loadDictData()
    this.listRepairPools()
    this.listRepairSettings()
  },
  methods: {
    async loadDictData() {
      try {
        this.states = [{
          value: 'waiting',
          label: this.$t('repairReturnVisit.waiting')
        }, {
          value: 'finish',
          label: this.$t('repairReturnVisit.finish')
        }]
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    async listRepairPools() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size,
          communityId: this.getCommunityId()
        }

        // 清理空值
        Object.keys(params).forEach(key => {
          if (params[key] === '') delete params[key]
        })

        const { data, total } = await getRepairReturnVisitList(params)
        this.repairPools = data
        this.pagination.total = total
      } catch (error) {
        console.error('查询报修回访列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('repairReturnVisit.page.fetchError')
      } finally {
        this.loading = false
      }
    },
    async listRepairSettings() {
      try {
        const { data } = await listRepairSettings({
          page: 1,
          row: 50,
          communityId: this.getCommunityId()
        })
        this.repairSettings = data
      } catch (error) {
        console.error('查询报修类型设置失败:', error)
      }
    },
    queryRepairPool() {
      this.pagination.current = 1
      this.listRepairPools()
    },
    resetRepairPool() {
      this.searchForm = {
        repairId: '',
        repairType: '',
        repairName: '',
        tel: '',
        state: ''
      }
      this.queryRepairPool()
    },
    openRepairDetail(repairPool) {
      this.$router.push({
        path: '/views/work/repairDetail',
        query: { repairId: repairPool.repairId }
      })
    },
    openEditOwnerRepair(repairPool) {
      this.$refs.visitModal.open(repairPool)
    },
    handleSizeChange(size) {
      this.pagination.size = size
      this.listRepairPools()
    },
    handleCurrentChange(current) {
      this.pagination.current = current
      this.listRepairPools()
    }
  }
}
</script>

<style lang="scss" scoped>
.repair-return-visit-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}
</style>
